import FileSelectionModal from "./FileSelectionModal";
import type { FileSelectionModalProps } from "./types";

/** Height for the agents pickers (#11045).
 *
 * `min-height` beats `height` in CSS, so the panel is 80vh on tall viewports
 * but grows toward the full viewport when 80vh would be too short (browser
 * zoom shrinks the viewport in CSS pixels). The 52rem cap keeps it from
 * turning into a near-fullscreen sheet on mid-height windows, and the
 * `100vh - 2rem` arm guarantees it never extends past the viewport.
 */
const AGENTS_HEIGHT_CLASS = "h-[80vh] min-h-[min(52rem,100vh_-_2rem)]";

/** The document picker a conversation's composer opens: chat's and the
 * agents' (#12195 round 3, Hunter: the two must be literally the same modal
 * apart from size). One component so they cannot drift. It renders the
 * shared `FileSelectionModal` with chat's configuration: upload from the
 * picker with duplicate reuse, set here, and the modal's own defaults for
 * everything it does not set (the title, the confirm label, the width, the
 * confirm gate, the file kinds), which is why no line here names them.
 * Height is the one thing a host chooses, from the two the system has.
 * Anything else a host wants is a change here, for both products.
 */
const AttachmentPicker = ({
  isOpen,
  onClose,
  onConfirmWithSelection,
  initialFileIds,
  initialFolderPaths,
  onUploadStarting,
  onUploadComplete,
  onUploadFailed,
  isPending,
  height,
}: Pick<
  FileSelectionModalProps,
  | "isOpen"
  | "onClose"
  | "initialFileIds"
  | "initialFolderPaths"
  | "onUploadStarting"
  | "onUploadComplete"
  | "onUploadFailed"
  | "isPending"
> & {
  onConfirmWithSelection: NonNullable<
    FileSelectionModalProps["onConfirmWithSelection"]
  >;
  /** The one host-specific setting: the agents pickers keep their taller
   * minimum (#11045); chat's take the modal's default. */
  height?: "agents";
}) => (
  <FileSelectionModal
    isOpen={isOpen}
    onClose={onClose}
    onConfirmWithSelection={onConfirmWithSelection}
    initialFileIds={initialFileIds}
    initialFolderPaths={initialFolderPaths}
    enableUpload
    reuseDuplicateUploads
    onUploadStarting={onUploadStarting}
    onUploadComplete={onUploadComplete}
    onUploadFailed={onUploadFailed}
    isPending={isPending}
    heightClass={height === "agents" ? AGENTS_HEIGHT_CLASS : undefined}
  />
);

export default AttachmentPicker;
